import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Calendar, ChevronDown, DollarSign, TrendingUp, ArrowUp, ArrowDown, Filter, Award } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useMarketingEmbedded } from '../hooks/useMarketingEmbedded';
import MarketingPageHeader from '../components/marketing/MarketingPageHeader';
import PortalBackground from '../components/PortalBackground';

const LeadROIDashboard = ({ embedded: embeddedProp }) => {
  const embedded = useMarketingEmbedded(embeddedProp);
  const { currentUser } = useAuth();
  const [dateRange, setDateRange] = useState('This Month');
  const [activeTab, setActiveTab] = useState('sources');

  // Sample data for lead sources - in a real application this would come from your API
  const leadSourcesData = [
    { 
      id: 1, 
      name: 'Social Media', 
      expenditure: 5200, 
      qualifiedLeads: 48, 
      unqualifiedLeads: 124, 
      roi: 320,
      costPerQualifiedLead: 108.33
    },
    { 
      id: 2, 
      name: 'Email Marketing', 
      expenditure: 2800, 
      qualifiedLeads: 35, 
      unqualifiedLeads: 67, 
      roi: 275,
      costPerQualifiedLead: 80.00
    },
    { 
      id: 3, 
      name: 'Organic Search', 
      expenditure: 1200, 
      qualifiedLeads: 42, 
      unqualifiedLeads: 91, 
      roi: 840,
      costPerQualifiedLead: 28.57
    },
    { 
      id: 4, 
      name: 'Paid Search', 
      expenditure: 7500, 
      qualifiedLeads: 65, 
      unqualifiedLeads: 148, 
      roi: 240,
      costPerQualifiedLead: 115.38
    },
    { 
      id: 5, 
      name: 'Referral Program', 
      expenditure: 1800, 
      qualifiedLeads: 28, 
      unqualifiedLeads: 36, 
      roi: 420,
      costPerQualifiedLead: 64.29
    },
    { 
      id: 6, 
      name: 'Events & Webinars', 
      expenditure: 4500, 
      qualifiedLeads: 30, 
      unqualifiedLeads: 45, 
      roi: 180,
      costPerQualifiedLead: 150.00
    }
  ];

  // Sample data for ad campaigns
  const adCampaignsData = [
    { 
      id: 1, 
      name: 'Summer Promotion', 
      type: 'Awareness', 
      platform: 'Facebook', 
      expenditure: 3200, 
      qualifiedLeads: 28, 
      unqualifiedLeads: 86, 
      roi: 210,
      costPerQualifiedLead: 114.29
    },
    { 
      id: 2, 
      name: 'Product Launch', 
      type: 'Conversion', 
      platform: 'Google Ads', 
      expenditure: 4800, 
      qualifiedLeads: 42, 
      unqualifiedLeads: 95, 
      roi: 260,
      costPerQualifiedLead: 114.29
    },
    { 
      id: 3, 
      name: 'Retargeting', 
      type: 'Conversion', 
      platform: 'Instagram', 
      expenditure: 1500, 
      qualifiedLeads: 18, 
      unqualifiedLeads: 32, 
      roi: 330,
      costPerQualifiedLead: 83.33
    },
    { 
      id: 4, 
      name: 'Industry Newsletter', 
      type: 'Awareness', 
      platform: 'LinkedIn', 
      expenditure: 2200, 
      qualifiedLeads: 25, 
      unqualifiedLeads: 40, 
      roi: 290,
      costPerQualifiedLead: 88.00
    },
    { 
      id: 5, 
      name: 'Keyword Campaign', 
      type: 'Conversion', 
      platform: 'Google Ads', 
      expenditure: 2700, 
      qualifiedLeads: 23, 
      unqualifiedLeads: 52, 
      roi: 180,
      costPerQualifiedLead: 117.39
    },
    { 
      id: 6, 
      name: 'Video Campaign', 
      type: 'Engagement', 
      platform: 'YouTube', 
      expenditure: 3800, 
      qualifiedLeads: 32, 
      unqualifiedLeads: 64, 
      roi: 240,
      costPerQualifiedLead: 118.75
    }
  ];

  // Sort data by ROI for rankings
  const sortedLeadSources = [...leadSourcesData].sort((a, b) => b.roi - a.roi);
  const sortedAdCampaigns = [...adCampaignsData].sort((a, b) => b.roi - a.roi);

  // Calculate top performers for KPIs
  const topLeadSource = sortedLeadSources[0];
  const topAdCampaign = sortedAdCampaigns[0];
  
  // Calculate totals for lead sources
  const totalSourceExpenditure = leadSourcesData.reduce((sum, item) => sum + item.expenditure, 0);
  const totalSourceQualifiedLeads = leadSourcesData.reduce((sum, item) => sum + item.qualifiedLeads, 0);
  const totalSourceUnqualifiedLeads = leadSourcesData.reduce((sum, item) => sum + item.unqualifiedLeads, 0);
  const averageSourceROI = Math.round(leadSourcesData.reduce((sum, item) => sum + item.roi, 0) / leadSourcesData.length);
  const averageSourceCostPerQualifiedLead = Math.round(totalSourceExpenditure / totalSourceQualifiedLeads);

  // Calculate totals for ad campaigns
  const totalCampaignExpenditure = adCampaignsData.reduce((sum, item) => sum + item.expenditure, 0);
  const totalCampaignQualifiedLeads = adCampaignsData.reduce((sum, item) => sum + item.qualifiedLeads, 0);
  const totalCampaignUnqualifiedLeads = adCampaignsData.reduce((sum, item) => sum + item.unqualifiedLeads, 0);

  // Calculate overall average cost per lead
  const totalExpenditure = totalSourceExpenditure + totalCampaignExpenditure;
  const totalQualifiedLeads = totalSourceQualifiedLeads + totalCampaignQualifiedLeads;
  const overallAverageCostPerLead = totalQualifiedLeads > 0 ? totalExpenditure / totalQualifiedLeads : 0;

  // Save cost per lead to user's data
  useEffect(() => {
    const saveCostPerLead = async () => {
      if (!currentUser) return;

      try {
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          await updateDoc(userDocRef, {
            leadROIAnalysis: {
              costPerLead: overallAverageCostPerLead,
              lastUpdated: new Date().toISOString(),
              totalExpenditure: totalExpenditure,
              totalQualifiedLeads: totalQualifiedLeads,
              averageSourceROI: averageSourceROI,
              averageSourceCostPerQualifiedLead: averageSourceCostPerQualifiedLead
            }
          });
        } else {
          await setDoc(userDocRef, {
            leadROIAnalysis: {
              costPerLead: overallAverageCostPerLead,
              lastUpdated: new Date().toISOString(),
              totalExpenditure: totalExpenditure,
              totalQualifiedLeads: totalQualifiedLeads,
              averageSourceROI: averageSourceROI,
              averageSourceCostPerQualifiedLead: averageSourceCostPerQualifiedLead
            }
          });
        }
      } catch (error) {
        console.error('Error saving cost per lead:', error);
      }
    };

    saveCostPerLead();
  }, [currentUser, overallAverageCostPerLead, totalExpenditure, totalQualifiedLeads, averageSourceROI, averageSourceCostPerQualifiedLead]);
  
  // Format currency
  const formatCurrency = (value) => {
    return `$${value.toLocaleString()}`;
  };

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

  return (
    <div className={embedded ? '' : 'relative min-h-screen'}>
      {!embedded && <PortalBackground />}
      <div className={embedded ? '' : 'relative'}>
      {!embedded && <MarketingPageHeader title="Lead ROI Dashboard" />}
      <div className={`${embedded ? 'mb-4' : 'bg-white/80 dark:bg-gray-900/80 border-b border-gray-200 dark:border-gray-800'}`}>
        <div className={`${embedded ? '' : 'px-4 py-6'} mx-auto max-w-7xl sm:px-6 lg:px-8`}>
          {embedded && (
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Lead ROI Dashboard</h1>
          )}
          <div className="flex items-center justify-end">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <button className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none">
                  <Calendar className="w-4 h-4 mr-2" />
                  {dateRange}
                  <ChevronDown className="w-4 h-4 ml-2" />
                </button>
              </div>
              <button className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none">
                Export Report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Top Performers Cards */}
        <div className="grid grid-cols-1 gap-5 mb-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="p-5 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-yellow-500 rounded-md">
                <Award className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 w-0 ml-5">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Top Lead Source</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{topLeadSource.name}</div>
                  </dd>
                  <dd className="flex items-baseline mt-1">
                    <div className="text-sm font-medium text-green-600">{topLeadSource.roi}% ROI</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="p-5 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-blue-500 rounded-md">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 w-0 ml-5">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Best Cost Per Qualified Lead</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{formatCurrency(sortedLeadSources[0].costPerQualifiedLead)}</div>
                  </dd>
                  <dd className="flex items-baseline mt-1">
                    <div className="text-sm font-medium text-green-600">{sortedLeadSources[0].name}</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="p-5 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-green-500 rounded-md">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 w-0 ml-5">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Top Ad Campaign</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{topAdCampaign.name}</div>
                  </dd>
                  <dd className="flex items-baseline mt-1">
                    <div className="text-sm font-medium text-green-600">{topAdCampaign.roi}% ROI</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="p-5 overflow-hidden rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
            <div className="flex items-center">
              <div className="flex-shrink-0 p-3 bg-purple-500 rounded-md">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 w-0 ml-5">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Expenditure</dt>
                  <dd className="flex items-baseline">
                    <div className="text-2xl font-semibold text-gray-900">{formatCurrency(totalSourceExpenditure + totalCampaignExpenditure)}</div>
                  </dd>
                  <dd className="flex items-baseline mt-1">
                    <div className="text-sm font-medium text-green-600">{totalSourceQualifiedLeads + totalCampaignQualifiedLeads} Qualified Leads</div>
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex -mb-px space-x-8">
            <button
              onClick={() => setActiveTab('sources')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sources'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Lead Sources
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'campaigns'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Ad Campaigns
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'comparison'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              ROI Comparison
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        <div className="space-y-6">
          {activeTab === 'sources' && (
            <>
              {/* Lead Sources Overview */}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                  <h2 className="mb-4 text-lg font-medium text-gray-900">Lead Sources ROI</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={sortedLeadSources} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'roi') return [`${value}%`, 'ROI'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="roi" name="ROI %" fill="#10B981" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                  <h2 className="mb-4 text-lg font-medium text-gray-900">Expenditure vs. Qualified Leads</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={leadSourcesData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis yAxisId="left" orientation="left" stroke="#6366F1" />
                      <YAxis yAxisId="right" orientation="right" stroke="#F59E0B" />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'expenditure') return [formatCurrency(value), 'Expenditure'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar yAxisId="left" dataKey="expenditure" name="Expenditure" fill="#6366F1" />
                      <Bar yAxisId="right" dataKey="qualifiedLeads" name="Qualified Leads" fill="#F59E0B" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Lead Sources Table */}
              <div className="p-5 mt-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Lead Sources Performance</h2>
                  <div className="flex space-x-2">
                    <button className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50">
                      <Filter className="w-4 h-4 mr-2" />
                      Filter
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Lead Source
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Expenditure
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Qualified Leads
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Unqualified Leads
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Cost/Qualified Lead
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          ROI
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedLeadSources.map((source, index) => (
                        <tr key={source.id} className={index < 3 ? "bg-green-50" : ""}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {index < 3 && <Award className="w-4 h-4 mr-2 text-yellow-500" />}
                              <div className="text-sm font-medium text-gray-900">{source.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatCurrency(source.expenditure)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{source.qualifiedLeads}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{source.unqualifiedLeads}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatCurrency(source.costPerQualifiedLead)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {source.roi}%
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-medium">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">TOTAL / AVERAGE</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(totalSourceExpenditure)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{totalSourceQualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{totalSourceUnqualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(averageSourceCostPerQualifiedLead)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {averageSourceROI}%
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'campaigns' && (
            <>
              {/* Charts for Ad Campaigns */}
              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                  <h2 className="mb-4 text-lg font-medium text-gray-900">Campaign ROI by Platform</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={adCampaignsData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="platform" />
                      <YAxis />
                      <Tooltip 
                        formatter={(value, name) => {
                          if (name === 'roi') return [`${value}%`, 'ROI'];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Bar dataKey="roi" name="ROI %" fill="#8884d8" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                  <h2 className="mb-4 text-lg font-medium text-gray-900">Campaign Type Distribution</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={adCampaignsData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="expenditure"
                        nameKey="type"
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      >
                        {adCampaignsData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Ad Campaigns Table */}
              <div className="p-5 mt-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Ad Campaigns Performance</h2>
                  <div className="flex space-x-2">
                    <button className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50">
                      <Filter className="w-4 h-4 mr-2" />
                      Filter
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Campaign Name
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Type
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Platform
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Expenditure
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Qualified Leads
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Unqualified Leads
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Cost/Qualified Lead
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          ROI
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedAdCampaigns.map((campaign, index) => (
                        <tr key={campaign.id} className={index < 3 ? "bg-green-50" : ""}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {index < 3 && <Award className="w-4 h-4 mr-2 text-yellow-500" />}
                              <div className="text-sm font-medium text-gray-900">{campaign.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{campaign.type}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{campaign.platform}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatCurrency(campaign.expenditure)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{campaign.qualifiedLeads}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{campaign.unqualifiedLeads}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatCurrency(campaign.costPerQualifiedLead)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              {campaign.roi}%
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-gray-50 font-medium">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">TOTAL</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">-</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">-</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(totalCampaignExpenditure)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{totalCampaignQualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{totalCampaignUnqualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(Math.round(totalCampaignExpenditure / totalCampaignQualifiedLeads))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {Math.round(adCampaignsData.reduce((sum, item) => sum + item.roi, 0) / adCampaignsData.length)}%
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {activeTab === 'comparison' && (
            <>
              {/* Combined ROI Comparison */}
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <h2 className="mb-4 text-lg font-medium text-gray-900">ROI Comparison - All Channels</h2>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart 
                    data={[...sortedLeadSources, ...sortedAdCampaigns].sort((a, b) => b.roi - a.roi)}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" interval={0} angle={-45} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => {
                        if (name === 'roi') return [`${value}%`, 'ROI'];
                        if (name === 'expenditure') return [formatCurrency(value), 'Expenditure'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar dataKey="roi" name="ROI %" fill="#10B981" />
                    <Bar dataKey="expenditure" name="Expenditure" fill="#6366F1" />
                    <Bar dataKey="qualifiedLeads" name="Qualified Leads" fill="#F59E0B" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Cost per Qualified Lead Comparison */}
              <div className="grid grid-cols-1 gap-5 mt-6 lg:grid-cols-2">
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                  <h2 className="mb-4 text-lg font-medium text-gray-900">Cost per Qualified Lead - Lead Sources</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={[...leadSourcesData].sort((a, b) => a.costPerQualifiedLead - b.costPerQualifiedLead)} 
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="costPerQualifiedLead" name="Cost per Qualified Lead" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                  <h2 className="mb-4 text-lg font-medium text-gray-900">Cost per Qualified Lead - Ad Campaigns</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart 
                      data={[...adCampaignsData].sort((a, b) => a.costPerQualifiedLead - b.costPerQualifiedLead)} 
                      margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="costPerQualifiedLead" name="Cost per Qualified Lead" fill="#EF4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Qualified vs Unqualified Leads Summary */}
              <div className="p-5 mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <h2 className="mb-4 text-lg font-medium text-gray-900">Lead Quality Analysis</h2>
                <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                  <div>
                    <h3 className="mb-2 text-base font-medium text-gray-700">Lead Sources</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Qualified Leads', value: totalSourceQualifiedLeads },
                            { name: 'Unqualified Leads', value: totalSourceUnqualifiedLeads }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          <Cell fill="#10B981" />
                          <Cell fill="#EF4444" />
                        </Pie>
                        <Tooltip formatter={(value) => value} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div>
                    <h3 className="mb-2 text-base font-medium text-gray-700">Ad Campaigns</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Qualified Leads', value: totalCampaignQualifiedLeads },
                            { name: 'Unqualified Leads', value: totalCampaignUnqualifiedLeads }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        >
                          <Cell fill="#10B981" />
                          <Cell fill="#EF4444" />
                        </Pie>
                        <Tooltip formatter={(value) => value} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* ROI Summary Table */}
              <div className="p-5 mt-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
                <h2 className="mb-4 text-lg font-medium text-gray-900">ROI Summary</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Channel/Campaign
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Total Expenditure
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Qualified Leads
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          Cost/Qualified Lead
                        </th>
                        <th scope="col" className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                          ROI
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">All Lead Sources</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatCurrency(totalSourceExpenditure)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{totalSourceQualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatCurrency(Math.round(totalSourceExpenditure / totalSourceQualifiedLeads))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {averageSourceROI}%
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">All Ad Campaigns</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatCurrency(totalCampaignExpenditure)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{totalCampaignQualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatCurrency(Math.round(totalCampaignExpenditure / totalCampaignQualifiedLeads))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            {Math.round(adCampaignsData.reduce((sum, item) => sum + item.roi, 0) / adCampaignsData.length)}%
                          </div>
                        </td>
                      </tr>
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">OVERALL TOTAL</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(totalSourceExpenditure + totalCampaignExpenditure)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{totalSourceQualifiedLeads + totalCampaignQualifiedLeads}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{formatCurrency(Math.round((totalSourceExpenditure + totalCampaignExpenditure) / (totalSourceQualifiedLeads + totalCampaignQualifiedLeads)))}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {Math.round((averageSourceROI + Math.round(adCampaignsData.reduce((sum, item) => sum + item.roi, 0) / adCampaignsData.length)) / 2)}%
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

export default LeadROIDashboard; 